import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { NotificationService } from '../../../application/services/NotificationService';
import { MessagingApiService } from '../../services/messaging-api/MessagingApiService';
import {
  MessagingContext,
  SaveTelegramAccountData,
  SaveTelegramBotData,
  SaveTelegramParserData,
  TelegramAccount,
  TelegramAuthStatus,
  TelegramBot,
  TelegramParser,
  TelegramParserSourceChannel,
  TelegramSendCodeResult,
} from '../../types/Messaging';

type TelegramPageMode = 'list' | 'account-new' | 'account-edit' | 'bot-new' | 'bot-edit' | 'parser-new' | 'parser-edit';

interface ParserSourceChannelFormRow {
  formId: string;
  chatId: string;
  label: string;
  draftChatId: string;
  draftLabel: string;
  isEditing: boolean;
}

@Component({
  selector: 'app-telegram-page',
  standalone: false,
  templateUrl: './telegram-page.component.html',
  styleUrl: './telegram-page.component.sass',
})
export class TelegramPageComponent implements OnInit {
  private readonly defaultParserSourceLabels: Record<string, string> = {
    '-1001284634752': 'ХК «Автомобилист»',
  };
  private readonly defaultParserSourceChatIds = [
    '-1001227991890',
    '-1001230128639',
    '-1001284634752',
    '-1001441302132',
    '-1001140635550',
    '-1001135872665',
    '-1001344166314',
    '-1001548942633',
    '-1001483462480',
    '-1001368222954',
    '-1001129529511',
    '-1001365784828',
    '-1001087632472',
    '-1001221018489',
    '-1001553061055',
    '-1002310580491',
  ];
  private parserSourceChannelFormSequence = 0;

  public context: MessagingContext = {
    users: [],
    teams: [],
    telegramAccounts: [],
    telegramBots: [],
    telegramParsers: [],
  };
  public accounts: TelegramAccount[] = [];
  public bots: TelegramBot[] = [];
  public parsers: TelegramParser[] = [];
  public formData: SaveTelegramAccountData = this.createEmptyForm();
  public botFormData: SaveTelegramBotData = this.createEmptyBotForm();
  public parserFormData: SaveTelegramParserData = this.createEmptyParserForm();
  public parserSourceChannels: ParserSourceChannelFormRow[] = this.createParserSourceChannelRows(this.parserFormData.sourceChannels ?? []);
  public botAdminIdsText = '';
  public parserKeywordsText = '';
  public mode: TelegramPageMode = 'list';
  public editingAccount: TelegramAccount | null = null;
  public editingBot: TelegramBot | null = null;
  public editingParser: TelegramParser | null = null;
  public loading = false;
  public saving = false;
  public savingBot = false;
  public savingParser = false;
  public togglingParserIds: Record<string, boolean> = {};
  public authSendingCode = false;
  public authConfirming = false;
  public authAccount: TelegramAccount | null = null;
  public authCode = '';
  public authPassword = '';
  public authPasswordRequired = false;
  public authCodeInfo: TelegramSendCodeResult | null = null;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;

  public readonly authStatuses: Array<{ value: TelegramAuthStatus; label: string }> = [
    { value: 'pending_authorization', label: 'Ожидает авторизации' },
    { value: 'authorized', label: 'Авторизован' },
    { value: 'revoked', label: 'Отозван' },
    { value: 'error', label: 'Ошибка' },
  ];

  public constructor(
    private messagingApi: MessagingApiService,
    private notificationService: NotificationService,
    private router: Router,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  public get activeAccountsCount(): number {
    return this.accounts.filter((account) => account.authStatus === 'authorized' && account.sessionStringConfigured).length;
  }

  public get activeBotsCount(): number {
    return this.bots.filter((bot) => bot.isActive && bot.tokenConfigured).length;
  }

  public get activeParsersCount(): number {
    return this.parsers.filter((parser) => parser.isActive).length;
  }

  public get formTitle(): string {
    return this.editingAccount ? 'Редактирование аккаунта' : 'Новый Telegram-аккаунт';
  }

  public get botFormTitle(): string {
    return this.editingBot ? 'Редактирование бота' : 'Новый Telegram-бот';
  }

  public get parserFormTitle(): string {
    return this.editingParser ? 'Редактирование парсера' : 'Новый Telegram-парсер';
  }

  public get pageTitle(): string {
    if (this.isAccountEditorMode) {
      return this.formTitle;
    }

    if (this.isBotEditorMode) {
      return this.botFormTitle;
    }

    if (this.isParserEditorMode) {
      return this.parserFormTitle;
    }

    return 'Telegram';
  }

  public get pageDescription(): string {
    if (this.isAccountEditorMode) {
      return 'Настройка личного GramJS-аккаунта для рассылок и авторизации отправителя.';
    }

    if (this.isBotEditorMode) {
      return 'Настройка бота дашборда и списка Telegram ID администраторов.';
    }

    if (this.isParserEditorMode) {
      return 'Настройка каналов-источников, ключевых слов и целевого канала для пересылки найденных сообщений.';
    }

    return 'Личные аккаунты GramJS, бот дашборда и парсеры Telegram-каналов.';
  }

  public get isListMode(): boolean {
    return this.mode === 'list';
  }

  public get isAccountEditorMode(): boolean {
    return this.mode === 'account-new' || this.mode === 'account-edit';
  }

  public get isBotEditorMode(): boolean {
    return this.mode === 'bot-new' || this.mode === 'bot-edit';
  }

  public get isParserEditorMode(): boolean {
    return this.mode === 'parser-new' || this.mode === 'parser-edit';
  }

  public get accountSubmitLabel(): string {
    if (this.saving) {
      return 'Сохраняем...';
    }

    return this.editingAccount ? 'Сохранить аккаунт' : 'Добавить аккаунт';
  }

  public get botSubmitLabel(): string {
    if (this.savingBot) {
      return 'Сохраняем...';
    }

    return this.editingBot ? 'Сохранить бота' : 'Добавить бота';
  }

  public get parserSubmitLabel(): string {
    if (this.savingParser) {
      return 'Сохраняем...';
    }

    return this.editingParser ? 'Сохранить парсер' : 'Добавить парсер';
  }

  public async saveAccount(): Promise<void> {
    this.errorMessage = this.validateForm();
    if (this.errorMessage) {
      return;
    }

    this.saving = true;
    this.successMessage = null;
    try {
      const payload = this.editingAccount ? this.prepareUpdateData() : this.prepareCreateData();
      const response = this.editingAccount
        ? await this.messagingApi.updateTelegramAccount(this.editingAccount.id, payload)
        : await this.messagingApi.createTelegramAccount(payload as SaveTelegramAccountData);
      const account = this.unwrapResponse(response);
      if (!account) {
        return;
      }

      this.successMessage = this.editingAccount ? 'Telegram-аккаунт обновлен.' : 'Telegram-аккаунт добавлен.';
      this.notificationService.notify('telegram', this.successMessage, this.getAccountTitle(account));
      this.resetAccountEditor();
      await this.router.navigate(['/dashboard/telegram']);
      await this.loadData(false);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public startEdit(account: TelegramAccount): void {
    this.populateAccountForm(account);
  }

  public cancelEdit(): void {
    this.resetAccountEditor();
    void this.router.navigate(['/dashboard/telegram']);
  }

  private populateAccountForm(account: TelegramAccount): void {
    this.editingAccount = account;
    this.formData = {
      userId: account.userId,
      label: account.label,
      apiId: account.apiId,
      apiHash: null,
      phone: null,
      sessionString: null,
      authStatus: account.authStatus,
    };
    this.successMessage = null;
    this.errorMessage = null;
  }

  private resetAccountEditor(): void {
    this.editingAccount = null;
    this.formData = this.createEmptyForm();
    this.applyDefaultUser();
  }

  public async deleteAccount(account: TelegramAccount): Promise<void> {
    if (!window.confirm(`Удалить Telegram-аккаунт ${account.label}?`)) {
      return;
    }

    const isDeletingCurrentEditor = this.editingAccount?.id === account.id;
    try {
      const response = await this.messagingApi.deleteTelegramAccount(account.id);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }
      this.successMessage = 'Telegram-аккаунт удален.';
      this.notificationService.notify('telegram', 'Telegram-аккаунт удален', account.label);
      if (isDeletingCurrentEditor) {
        this.resetAccountEditor();
        await this.router.navigate(['/dashboard/telegram']);
      }
      await this.loadData(false);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public async saveBot(): Promise<void> {
    this.errorMessage = this.validateBotForm();
    if (this.errorMessage) {
      return;
    }

    this.savingBot = true;
    this.successMessage = null;
    try {
      const payload = this.editingBot ? this.prepareBotUpdateData() : this.prepareBotCreateData();
      const response = this.editingBot
        ? await this.messagingApi.updateTelegramBot(this.editingBot.id, payload)
        : await this.messagingApi.createTelegramBot(payload as SaveTelegramBotData);
      const bot = this.unwrapResponse(response);
      if (!bot) {
        return;
      }

      this.successMessage = this.editingBot ? 'Telegram-бот обновлен.' : 'Telegram-бот добавлен.';
      this.notificationService.notify('telegram', this.successMessage, bot.label);
      this.resetBotEditor();
      await this.router.navigate(['/dashboard/telegram']);
      await this.loadData(false);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.savingBot = false;
      this.changeDetector.detectChanges();
    }
  }

  public startEditBot(bot: TelegramBot): void {
    this.populateBotForm(bot);
  }

  public cancelBotEdit(): void {
    this.resetBotEditor();
    void this.router.navigate(['/dashboard/telegram']);
  }

  private populateBotForm(bot: TelegramBot): void {
    this.editingBot = bot;
    this.botFormData = {
      label: bot.label,
      username: bot.username,
      token: null,
      isActive: bot.isActive,
      adminTelegramIds: [...bot.adminTelegramIds],
    };
    this.botAdminIdsText = bot.adminTelegramIds.join('\n');
    this.successMessage = null;
    this.errorMessage = null;
  }

  private resetBotEditor(): void {
    this.editingBot = null;
    this.botFormData = this.createEmptyBotForm();
    this.botAdminIdsText = this.getDefaultAdminIdsText();
  }

  public async deleteBot(bot: TelegramBot): Promise<void> {
    if (!window.confirm(`Удалить Telegram-бот ${bot.label}?`)) {
      return;
    }

    const isDeletingCurrentEditor = this.editingBot?.id === bot.id;
    try {
      const response = await this.messagingApi.deleteTelegramBot(bot.id);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }
      this.successMessage = 'Telegram-бот удален.';
      this.notificationService.notify('telegram', 'Telegram-бот удален', bot.label);
      if (isDeletingCurrentEditor) {
        this.resetBotEditor();
        await this.router.navigate(['/dashboard/telegram']);
      }
      await this.loadData(false);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public async saveParser(): Promise<void> {
    this.errorMessage = this.validateParserForm();
    if (this.errorMessage) {
      return;
    }

    this.savingParser = true;
    this.successMessage = null;
    try {
      const payload = this.editingParser ? this.prepareParserUpdateData() : this.prepareParserCreateData();
      const response = this.editingParser
        ? await this.messagingApi.updateTelegramParser(this.editingParser.id, payload)
        : await this.messagingApi.createTelegramParser(payload as SaveTelegramParserData);
      const parser = this.unwrapResponse(response);
      if (!parser) {
        return;
      }

      this.successMessage = this.editingParser ? 'Telegram-парсер обновлен.' : 'Telegram-парсер добавлен.';
      this.notificationService.notify('telegram', this.successMessage, parser.label);
      this.resetParserEditor();
      await this.router.navigate(['/dashboard/telegram']);
      await this.loadData(false);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.savingParser = false;
      this.changeDetector.detectChanges();
    }
  }

  public startEditParser(parser: TelegramParser): void {
    this.populateParserForm(parser);
  }

  public cancelParserEdit(): void {
    this.resetParserEditor();
    void this.router.navigate(['/dashboard/telegram']);
  }

  private populateParserForm(parser: TelegramParser): void {
    this.editingParser = parser;
    this.parserFormData = {
      label: parser.label,
      telegramAccountId: parser.telegramAccountId,
      sourceChatIds: [...parser.sourceChatIds],
      sourceChannels: this.getParserSourceChannels(parser),
      targetChatId: parser.targetChatId,
      keywords: [...parser.keywords],
      isActive: parser.isActive,
      pollIntervalSeconds: parser.pollIntervalSeconds,
      historyLimit: parser.historyLimit,
    };
    this.parserSourceChannels = this.createParserSourceChannelRows(this.parserFormData.sourceChannels ?? []);
    this.parserKeywordsText = parser.keywords.join('\n');
    this.successMessage = null;
    this.errorMessage = null;
  }

  private resetParserEditor(): void {
    this.editingParser = null;
    this.parserFormData = this.createEmptyParserForm();
    this.applyDefaultParserAccount();
    this.parserSourceChannels = this.createParserSourceChannelRows(this.parserFormData.sourceChannels ?? []);
    this.parserKeywordsText = this.parserFormData.keywords.join('\n');
  }

  public async toggleParser(parser: TelegramParser): Promise<void> {
    this.togglingParserIds = {
      ...this.togglingParserIds,
      [parser.id]: true,
    };
    this.errorMessage = null;
    this.successMessage = null;

    try {
      const response = parser.isActive
        ? await this.messagingApi.stopTelegramParser(parser.id)
        : await this.messagingApi.startTelegramParser(parser.id);
      const updatedParser = this.unwrapResponse(response);
      if (!updatedParser) {
        return;
      }

      this.successMessage = updatedParser.isActive ? 'Парсер запущен.' : 'Парсер остановлен.';
      this.notificationService.notify('telegram', this.successMessage, updatedParser.label);
      await this.loadData(false);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.togglingParserIds = {
        ...this.togglingParserIds,
        [parser.id]: false,
      };
      this.changeDetector.detectChanges();
    }
  }

  public async deleteParser(parser: TelegramParser): Promise<void> {
    if (!window.confirm(`Удалить Telegram-парсер ${parser.label}?`)) {
      return;
    }

    const isDeletingCurrentEditor = this.editingParser?.id === parser.id;
    try {
      const response = await this.messagingApi.deleteTelegramParser(parser.id);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }
      this.successMessage = 'Telegram-парсер удален.';
      this.notificationService.notify('telegram', 'Telegram-парсер удален', parser.label);
      if (isDeletingCurrentEditor) {
        this.resetParserEditor();
        await this.router.navigate(['/dashboard/telegram']);
      }
      await this.loadData(false);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public async startAuthorization(account: TelegramAccount): Promise<void> {
    this.authSendingCode = true;
    this.authAccount = account;
    this.authCode = '';
    this.authPassword = '';
    this.authPasswordRequired = false;
    this.authCodeInfo = null;
    this.successMessage = null;
    this.errorMessage = null;
    try {
      const response = await this.messagingApi.sendTelegramAuthCode(account.id);
      const result = this.unwrapResponse(response);
      if (!result) {
        return;
      }
      this.authCodeInfo = result;
      this.successMessage = result.expiresAt
        ? `Код отправлен ${result.isCodeViaApp ? 'в Telegram' : 'по SMS'}.`
        : 'Аккаунт уже авторизован.';
      this.notificationService.notify('telegram', 'Код авторизации отправлен', account.label);
      await this.loadData(false);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.authSendingCode = false;
      this.changeDetector.detectChanges();
    }
  }

  public async confirmAuthorization(): Promise<void> {
    if (!this.authAccount) {
      return;
    }
    if (!this.authCode.trim()) {
      this.errorMessage = 'Введите код Telegram.';
      return;
    }

    this.authConfirming = true;
    this.successMessage = null;
    this.errorMessage = null;
    try {
      const response = await this.messagingApi.confirmTelegramAuthCode(
        this.authAccount.id,
        this.authCode,
        this.authPasswordRequired ? this.authPassword : null,
      );
      const result = this.unwrapResponse(response);
      if (!result) {
        return;
      }
      if (result.passwordRequired) {
        this.authPasswordRequired = true;
        this.successMessage = 'Введите облачный пароль Telegram для завершения авторизации.';
        return;
      }

      this.successMessage = 'Telegram-аккаунт авторизован.';
      this.notificationService.notify('telegram', 'Telegram-аккаунт авторизован', this.authAccount.label);
      this.cancelAuthorization();
      await this.loadData(false);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.authConfirming = false;
      this.changeDetector.detectChanges();
    }
  }

  public cancelAuthorization(): void {
    this.authAccount = null;
    this.authCode = '';
    this.authPassword = '';
    this.authPasswordRequired = false;
    this.authCodeInfo = null;
  }

  public getUserName(userId: string): string {
    const user = this.context.users.find((item) => item.id === userId);
    return user?.displayName || user?.username || 'Пользователь';
  }

  public getAuthStatusLabel(status: TelegramAuthStatus): string {
    return this.authStatuses.find((item) => item.value === status)?.label ?? status;
  }

  public getAccountTitle(account: TelegramAccount): string {
    const owner = account.user?.displayName || account.user?.username || this.getUserName(account.userId);
    return `${account.label} • ${owner}`;
  }

  public getParserAccountTitle(parser: TelegramParser): string {
    return parser.telegramAccount ? this.getAccountTitle(parser.telegramAccount) : 'Аккаунт удален';
  }

  public formatParserChannels(parser: TelegramParser): string {
    const channels = this.getParserSourceChannels(parser);
    const visible = channels.slice(0, 3).map((channel) => channel.label || channel.chatId).join(', ');
    return channels.length > 3 ? `${visible} +${(channels.length - 3).toString()}` : visible;
  }

  public formatDate(value: string | null): string {
    if (!value) {
      return 'Не проверялся';
    }

    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  public trackByFormId(_index: number, item: { formId: string }): string {
    return item.formId;
  }

  public addParserSourceChannel(): void {
    this.parserSourceChannels = [
      ...this.parserSourceChannels,
      this.createParserSourceChannelRow({ chatId: '', label: null }, true),
    ];
  }

  public editParserSourceChannel(row: ParserSourceChannelFormRow): void {
    this.parserSourceChannels = this.parserSourceChannels.map((item) => (
      item.formId === row.formId
        ? {
          ...item,
          draftChatId: item.chatId,
          draftLabel: item.label,
          isEditing: true,
        }
        : item
    ));
  }

  public applyParserSourceChannel(row: ParserSourceChannelFormRow): void {
    const chatId = row.draftChatId.trim();
    if (!chatId) {
      this.errorMessage = 'Укажите Telegram ID или @username канала.';
      return;
    }

    const label = row.draftLabel.trim();
    this.errorMessage = null;
    this.parserSourceChannels = this.parserSourceChannels.map((item) => (
      item.formId === row.formId
        ? {
          ...item,
          chatId,
          label,
          draftChatId: chatId,
          draftLabel: label,
          isEditing: false,
        }
        : item
    ));
  }

  public cancelParserSourceChannelEdit(row: ParserSourceChannelFormRow): void {
    if (!row.chatId.trim() && !row.label.trim()) {
      this.removeParserSourceChannel(row);
      return;
    }

    this.parserSourceChannels = this.parserSourceChannels.map((item) => (
      item.formId === row.formId
        ? {
          ...item,
          draftChatId: item.chatId,
          draftLabel: item.label,
          isEditing: false,
        }
        : item
    ));
  }

  public removeParserSourceChannel(row: ParserSourceChannelFormRow): void {
    this.parserSourceChannels = this.parserSourceChannels.filter((item) => item.formId !== row.formId);
  }

  public getParserSourceChannelTitle(row: ParserSourceChannelFormRow): string {
    return row.label || 'Комментарий не задан';
  }

  private async loadData(showLoader = true): Promise<void> {
    this.loading = showLoader;
    this.errorMessage = null;
    try {
      const context = this.unwrapResponse(await this.messagingApi.getContext());
      if (!context) {
        return;
      }
      this.context = context;
      this.accounts = context.telegramAccounts;
      this.bots = context.telegramBots;
      this.parsers = context.telegramParsers;
      this.syncModeFromRoute();
      if (!this.botAdminIdsText && !this.editingBot) {
        this.botAdminIdsText = this.getDefaultAdminIdsText();
        this.botFormData.adminTelegramIds = this.parseAdminIds(this.botAdminIdsText);
      }
      if (!this.editingParser && !this.parserFormData.telegramAccountId) {
        this.applyDefaultParserAccount();
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private applyDefaultUser(): void {
    if (!this.formData.userId) {
      this.formData.userId = this.context.users[0]?.id ?? '';
    }
  }

  private applyDefaultParserAccount(): void {
    if (!this.parserFormData.telegramAccountId) {
      this.parserFormData.telegramAccountId = this.accounts.find((account) => account.authStatus === 'authorized' && account.sessionStringConfigured)?.id ?? '';
    }
  }

  private syncModeFromRoute(): void {
    const path = this.router.url.split(/[?#]/)[0];
    const accountEditMatch = path.match(/\/dashboard\/telegram\/accounts\/([^/]+)\/edit$/);
    const botEditMatch = path.match(/\/dashboard\/telegram\/bots\/([^/]+)\/edit$/);
    const parserEditMatch = path.match(/\/dashboard\/telegram\/parsers\/([^/]+)\/edit$/);

    if (path.endsWith('/dashboard/telegram/accounts/new')) {
      this.mode = 'account-new';
      this.resetAccountEditor();
      return;
    }

    if (accountEditMatch) {
      const account = this.accounts.find((item) => item.id === accountEditMatch[1]);
      if (!account) {
        this.mode = 'list';
        this.resetAccountEditor();
        this.errorMessage = 'Telegram-аккаунт не найден.';
        return;
      }

      this.mode = 'account-edit';
      this.populateAccountForm(account);
      return;
    }

    if (path.endsWith('/dashboard/telegram/bots/new')) {
      this.mode = 'bot-new';
      this.resetBotEditor();
      return;
    }

    if (botEditMatch) {
      const bot = this.bots.find((item) => item.id === botEditMatch[1]);
      if (!bot) {
        this.mode = 'list';
        this.resetBotEditor();
        this.errorMessage = 'Telegram-бот не найден.';
        return;
      }

      this.mode = 'bot-edit';
      this.populateBotForm(bot);
      return;
    }

    if (path.endsWith('/dashboard/telegram/parsers/new')) {
      this.mode = 'parser-new';
      this.resetParserEditor();
      return;
    }

    if (parserEditMatch) {
      const parser = this.parsers.find((item) => item.id === parserEditMatch[1]);
      if (!parser) {
        this.mode = 'list';
        this.resetParserEditor();
        this.errorMessage = 'Telegram-парсер не найден.';
        return;
      }

      this.mode = 'parser-edit';
      this.populateParserForm(parser);
      return;
    }

    this.mode = 'list';
    this.editingAccount = null;
    this.editingBot = null;
    this.editingParser = null;
  }

  private validateForm(): string | null {
    if (!this.formData.userId) {
      return 'Выберите пользователя.';
    }
    if (!this.formData.label.trim()) {
      return 'Укажите название аккаунта.';
    }
    if (!this.formData.apiId || this.formData.apiId <= 0) {
      return 'Укажите Telegram api_id.';
    }
    if (!this.editingAccount && !this.formData.apiHash?.trim()) {
      return 'Укажите Telegram api_hash.';
    }
    if (!this.editingAccount && !this.formData.phone?.trim()) {
      return 'Укажите телефон Telegram.';
    }
    return null;
  }

  private validateBotForm(): string | null {
    if (!this.botFormData.label.trim()) {
      return 'Укажите название Telegram-бота.';
    }
    if (!this.editingBot && !this.botFormData.token?.trim()) {
      return 'Укажите token Telegram-бота.';
    }
    return null;
  }

  private validateParserForm(): string | null {
    if (!this.parserFormData.label.trim()) {
      return 'Укажите название парсера.';
    }
    if (!this.parserFormData.telegramAccountId) {
      return 'Выберите Telegram-аккаунт парсера.';
    }
    const sourceChannelsError = this.validateParserSourceChannels();
    if (sourceChannelsError) {
      return sourceChannelsError;
    }
    if (!this.parserFormData.targetChatId.trim()) {
      return 'Укажите целевой канал.';
    }
    if (this.parseTextList(this.parserKeywordsText).length === 0) {
      return 'Добавьте хотя бы одно ключевое слово.';
    }
    return null;
  }

  private prepareCreateData(): SaveTelegramAccountData {
    return {
      ...this.formData,
      apiHash: this.formData.apiHash?.trim() || null,
      phone: this.formData.phone?.trim() || null,
    };
  }

  private prepareUpdateData(): Partial<SaveTelegramAccountData> {
    return {
      userId: this.formData.userId,
      label: this.formData.label,
      apiId: this.formData.apiId,
      apiHash: this.formData.apiHash?.trim() || undefined,
      phone: this.formData.phone?.trim() || undefined,
      sessionString: this.formData.sessionString?.trim() || undefined,
      authStatus: this.formData.authStatus,
    };
  }

  private prepareBotCreateData(): SaveTelegramBotData {
    return {
      ...this.botFormData,
      token: this.botFormData.token?.trim() || null,
      adminTelegramIds: this.parseAdminIds(this.botAdminIdsText),
    };
  }

  private prepareBotUpdateData(): Partial<SaveTelegramBotData> {
    return {
      label: this.botFormData.label,
      username: this.botFormData.username?.trim() || null,
      token: this.botFormData.token?.trim() || undefined,
      isActive: this.botFormData.isActive,
      adminTelegramIds: this.parseAdminIds(this.botAdminIdsText),
    };
  }

  private prepareParserCreateData(): SaveTelegramParserData {
    const sourceChannels = this.prepareParserSourceChannels();

    return {
      ...this.parserFormData,
      label: this.parserFormData.label.trim(),
      sourceChatIds: sourceChannels.map((channel) => channel.chatId),
      sourceChannels,
      targetChatId: this.parserFormData.targetChatId.trim(),
      keywords: this.parseTextList(this.parserKeywordsText),
      pollIntervalSeconds: Number(this.parserFormData.pollIntervalSeconds) || 15,
      historyLimit: Number(this.parserFormData.historyLimit) || 50,
    };
  }

  private prepareParserUpdateData(): Partial<SaveTelegramParserData> {
    return this.prepareParserCreateData();
  }

  private createEmptyForm(): SaveTelegramAccountData {
    return {
      userId: '',
      label: '',
      apiId: null,
      apiHash: '',
      phone: '',
      sessionString: '',
      authStatus: 'pending_authorization',
    };
  }

  private createEmptyBotForm(): SaveTelegramBotData {
    return {
      label: '',
      username: '',
      token: '',
      isActive: true,
      adminTelegramIds: [],
    };
  }

  private createEmptyParserForm(): SaveTelegramParserData {
    return {
      label: '',
      telegramAccountId: '',
      sourceChatIds: [...this.defaultParserSourceChatIds],
      sourceChannels: this.createDefaultParserSourceChannels(),
      targetChatId: '-1002356062714',
      keywords: [
        'билет',
        'билеты',
        'билетов',
        'билетами',
      ],
      isActive: false,
      pollIntervalSeconds: 15,
      historyLimit: 50,
    };
  }

  private createDefaultParserSourceChannels(): TelegramParserSourceChannel[] {
    return this.defaultParserSourceChatIds.map((chatId) => ({
      chatId,
      label: this.defaultParserSourceLabels[chatId] ?? null,
    }));
  }

  private createParserSourceChannelRows(
    sourceChannels: TelegramParserSourceChannel[],
    isEditing = false,
  ): ParserSourceChannelFormRow[] {
    return sourceChannels.map((channel) => this.createParserSourceChannelRow(channel, isEditing));
  }

  private createParserSourceChannelRow(
    channel: TelegramParserSourceChannel,
    isEditing = false,
  ): ParserSourceChannelFormRow {
    const chatId = channel.chatId.trim();
    const label = channel.label?.trim() ?? '';

    return {
      formId: `source-${(this.parserSourceChannelFormSequence += 1).toString()}`,
      chatId,
      label,
      draftChatId: chatId,
      draftLabel: label,
      isEditing,
    };
  }

  private getParserSourceChannels(parser: TelegramParser): TelegramParserSourceChannel[] {
    return Array.isArray(parser.sourceChannels) && parser.sourceChannels.length > 0
      ? parser.sourceChannels
      : parser.sourceChatIds.map((chatId) => ({
        chatId,
        label: this.defaultParserSourceLabels[chatId] ?? null,
      }));
  }

  private validateParserSourceChannels(): string | null {
    if (this.parserSourceChannels.length === 0) {
      return 'Добавьте хотя бы один канал-источник.';
    }

    const hasEmptyChatId = this.parserSourceChannels.some((row) => {
      const chatId = (row.isEditing ? row.draftChatId : row.chatId).trim();
      const label = (row.isEditing ? row.draftLabel : row.label).trim();
      return !chatId && Boolean(label);
    });
    if (hasEmptyChatId) {
      return 'Укажите Telegram ID или @username у каждого подписанного источника.';
    }

    if (this.prepareParserSourceChannels().length === 0) {
      return 'Добавьте хотя бы один канал-источник.';
    }

    return null;
  }

  private prepareParserSourceChannels(): TelegramParserSourceChannel[] {
    const channelByChatId = new Map<string, TelegramParserSourceChannel>();

    this.parserSourceChannels.forEach((row) => {
      const chatId = (row.isEditing ? row.draftChatId : row.chatId).trim();
      if (!chatId) {
        return;
      }
      const label = (row.isEditing ? row.draftLabel : row.label).trim() || null;
      const existingChannel = channelByChatId.get(chatId);
      if (!existingChannel) {
        channelByChatId.set(chatId, { chatId, label });
        return;
      }
      if (!existingChannel.label && label) {
        existingChannel.label = label;
      }
    });

    return [...channelByChatId.values()];
  }

  private parseTextList(value: string): string[] {
    return [...new Set(value
      .split(/[\n,;]+/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0))];
  }

  private parseAdminIds(value: string): string[] {
    return [...new Set(value
      .split(/[\s,;]+/)
      .map((item) => item.trim())
      .filter((item) => /^\d+$/.test(item)))];
  }

  private getDefaultAdminIdsText(): string {
    return this.context.users
      .map((user) => user.telegramId)
      .filter((telegramId): telegramId is string => Boolean(telegramId))
      .join('\n');
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private handleUnknownError(error: unknown): void {
    const apiErrorMessage = error instanceof HttpErrorResponse
      ? error.error?.errorMessage ?? error.error?.message
      : null;
    this.errorMessage = apiErrorMessage ?? (error instanceof Error ? error.message : 'Не удалось выполнить действие.');
  }
}
